import React, { useState, useEffect, useCallback } from "react";
import {
  Button,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  VStack,
  Text,
  Progress,
  Box,
  Checkbox,
  FormControl,
  Spinner,
  Alert,
  AlertIcon,
  Divider,
} from "@chakra-ui/react";
import { getBackendURL } from "./env";

// Small colored label per row status.
const STATUS_LABEL = {
  global: { text: "(global — not copied)", color: "purple.500" },
  missing: { text: "(referenced, missing from public — can't copy)", color: "red.500" },
};

export default function CreateSchemaModal({ isOpen, onClose, onSubmit }) {
  const [schemaName, setSchemaName] = useState("");
  const [copyData, setCopyData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState("");

  const [tables, setTables] = useState([]); // [{ table_name, referenced, copyable, detected, status, note }]
  const [checked, setChecked] = useState({}); // { table_name: bool }
  const [anomalies, setAnomalies] = useState(null);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [tablesError, setTablesError] = useState("");
  const toast = useToast();

  const loadTables = useCallback(async () => {
    setTablesLoading(true);
    setTablesError("");
    try {
      const res = await fetch(`${getBackendURL()}/api/admin/schema-tables/`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data.tables || [];
      setTables(list);
      setChecked(
        list.reduce((acc, t) => {
          acc[t.table_name] = !!t.detected; // pre-check copyable + referenced only
          return acc;
        }, {})
      );
      setAnomalies(data.anomalies || null);
    } catch (e) {
      console.error("Failed to load schema tables:", e);
      setTablesError(
        "Could not load the table list — the new schema will be created with ALL tables (the previous default)."
      );
      setTables([]);
      setChecked({});
      setAnomalies(null);
    } finally {
      setTablesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadTables();
      setSchemaName("");
      setCopyData(false);
    }
  }, [isOpen, loadTables]);

  const toggle = (name) =>
    setChecked((prev) => ({ ...prev, [name]: !prev[name] }));

  // Only copyable rows can be selected (non-copyable checkboxes are disabled).
  const copyableNames = new Set(tables.filter((t) => t.copyable).map((t) => t.table_name));
  const selectedTables = Object.keys(checked).filter((k) => checked[k] && copyableNames.has(k));
  const copyableCount = copyableNames.size;
  const detectedCount = tables.filter((t) => t.detected).length;

  const handleCreateSchema = async () => {
    if (!schemaName.trim()) return;
    // If the table list failed to load, fall back to the backend's legacy
    // copy-all behavior (tables_to_copy = null) rather than blocking the admin.
    const fallbackAll = !!tablesError;
    if (!fallbackAll && selectedTables.length === 0) {
      toast({
        title: "Select at least one table",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    setProgress(0);
    setCurrentStep("Creating schema...");

    try {
      await onSubmit(schemaName, copyData, fallbackAll ? null : selectedTables);
      setSchemaName("");
      setCopyData(false);
      onClose();
    } catch (error) {
      console.error("Error creating schema:", error);
    } finally {
      setLoading(false);
      setProgress(0);
      setCurrentStep("");
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Schema</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="gray.600">
              Creates a new schema with the selected tables, credit tracking, and
              security policies. Every table the codebase references is listed;
              tables used in code are pre-checked. Global tables (shared via
              public) and tables missing from public are shown but can't be copied.
            </Text>
            <Input
              placeholder="Enter schema name (e.g., Clinic6)"
              value={schemaName}
              onChange={(e) => setSchemaName(e.target.value)}
              isDisabled={loading}
            />
            <FormControl>
              <Checkbox
                isChecked={copyData}
                onChange={(e) => setCopyData(e.target.checked)}
                isDisabled={loading}
                colorScheme="blue"
              >
                Copy data from current schema (leave unchecked for empty tables)
              </Checkbox>
            </FormControl>

            <Divider />

            {tablesError && (
              <Alert status="warning" fontSize="sm">
                <AlertIcon />
                {tablesError}
              </Alert>
            )}

            {anomalies && anomalies.dynamic_unresolved?.length > 0 && (
              <Alert status="info" fontSize="xs">
                <AlertIcon />
                {anomalies.dynamic_unresolved.length} dynamic reference(s) the scanner
                could not resolve — review if a needed table looks unchecked.
              </Alert>
            )}

            <Text fontWeight="bold" fontSize="sm">
              Tables to copy ({selectedTables.length} of {copyableCount} copyable selected;{" "}
              {detectedCount} detected in code)
            </Text>

            {tablesLoading ? (
              <Spinner />
            ) : (
              <VStack align="stretch" spacing={1} maxH="320px" overflowY="auto">
                {tables.map((t) => {
                  const badge = STATUS_LABEL[t.status];
                  return (
                    <Checkbox
                      key={t.table_name}
                      isChecked={!!checked[t.table_name] && t.copyable}
                      onChange={() => toggle(t.table_name)}
                      isDisabled={loading || !t.copyable}
                      colorScheme={t.detected ? "green" : "gray"}
                    >
                      <Text as="span" color={t.copyable ? "inherit" : "gray.500"}>
                        {t.table_name}
                      </Text>{" "}
                      {badge ? (
                        <Text as="span" fontSize="xs" color={badge.color}>
                          {badge.text}
                        </Text>
                      ) : (
                        <Text
                          as="span"
                          fontSize="xs"
                          color={t.detected ? "green.600" : "gray.400"}
                        >
                          {t.detected ? "(used in code)" : "(not referenced)"}
                        </Text>
                      )}
                    </Checkbox>
                  );
                })}
              </VStack>
            )}

            {loading && (
              <Box w="100%">
                <Text fontSize="sm" mb={2}>{currentStep}</Text>
                <Progress value={progress} size="sm" colorScheme="blue" />
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={loading}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleCreateSchema}
            isLoading={loading}
            loadingText="Creating..."
            isDisabled={!schemaName.trim() || (!tablesError && selectedTables.length === 0)}
          >
            Create Schema
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
